import { Capacitor } from '@capacitor/core';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '../AppLayout/AppLayout';
import { Page404 } from '../Page404/Page404';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { useAppSelector } from '../../../Redux/hooks';
import { Home } from '../../MarketingArea/Home/Home';
import { Pricing } from '../../MarketingArea/Pricing/Pricing';
import { Welcome } from '../../MarketingArea/Welcome/Welcome';
import { Terms } from '../../MarketingArea/Terms/Terms';
import { Privacy } from '../../MarketingArea/Privacy/Privacy';
import { Refunds } from '../../MarketingArea/Refunds/Refunds';
import { AuthGate } from '../../UserArea/AuthGate/AuthGate';
import { Dashboard } from '../../UserArea/Dashboard/Dashboard';
import { Students } from '../../UserArea/Students/Students';
import { Ensembles } from '../../UserArea/Ensembles/Ensembles';
import { Lessons } from '../../UserArea/Lessons/Lessons';
import { Calendar } from '../../UserArea/Calendar/Calendar';
import { Profile } from '../../UserArea/Profile/Profile';
import { Receipts } from '../../UserArea/Receipts/Receipts';
import { ReceiptReports } from '../../UserArea/ReceiptReports/ReceiptReports';
import { BusinessSettings } from '../../UserArea/BusinessSettings/BusinessSettings';
import { OpenFormat } from '../../UserArea/OpenFormat/OpenFormat';
import { Admin } from '../../UserArea/Admin/Admin';
import { Premium } from '../../UserArea/Premium/Premium';
import { Reports } from '../../UserArea/Reports/Reports';
import { Reminders } from '../../UserArea/Reminders/Reminders';
import { PaymentDocuments } from '../../UserArea/PaymentDocuments/PaymentDocuments';
import { Contacts } from '../../UserArea/Contacts/Contacts';
import { Backup } from '../../UserArea/Backup/Backup';
import { GoogleCalendar } from '../../UserArea/GoogleCalendar/GoogleCalendar';
import { Payments } from '../../UserArea/Payments/Payments';
import { StudentProfile } from '../../UserArea/StudentProfile/StudentProfile';
import { PaymentRequest } from '../../PublicArea/PaymentRequest/PaymentRequest';
import { IntakeForm } from '../../PublicArea/IntakeForm/IntakeForm';
import { routes } from '../../../Utils/routes';
import {
  LegacyAppView,
  LegacyIntakeFormLink,
  LegacyPaymentRequestLink
} from './LegacyLinkRedirect';

/**
 * One application serves the marketing site, the tutor's app and the two
 * public documents.
 *
 * The token routes sit outside the authenticated tree deliberately: whoever
 * opens one has no account here. Everything under /user is behind AuthGate,
 * which is a convenience rather than a wall, because the API verifies the
 * token on every single request regardless of what was rendered.
 */
/**
 * The marketing home is the storefront for people who don't have the app
 * yet; someone who already has it does not need to be sold on it again every
 * time they open it. On native that is everyone. On the web it is whoever
 * Firebase restores a session for, and until that is known the page waits
 * rather than showing a sign-in button to a tutor who is already signed in.
 * Everything else about the marketing site (pricing, legal pages) stays
 * reachable from inside the app instead.
 */
function RootEntry() {
  const sessionStatus = useAppSelector(state => state.session.status);
  if (Capacitor.isNativePlatform() || sessionStatus === 'signed-in') {
    return <Navigate to={routes.app} replace />;
  }
  if (sessionStatus === 'unknown') {
    return <div className="auth-gate"><Spinner /></div>;
  }
  return <Home />;
}

export function AppRouting() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path={routes.home} element={<RootEntry />} />
        <Route path={routes.pricing} element={<Pricing />} />
        <Route path={routes.terms} element={<Terms />} />
        <Route path={routes.privacy} element={<Privacy />} />
        <Route path={routes.refunds} element={<Refunds />} />
        <Route path={routes.welcome} element={<Welcome />} />
        {/* /premium is where the old site sent people; the screen that
            answers it lives inside the app. */}
        <Route path={routes.premium} element={<Navigate to={routes.premiumArea} replace />} />

        {/* Every link the old application ever wrote carried its token in
            the hash, and those messages are in people's chat histories. */}
        <Route path="/pay" element={<LegacyPaymentRequestLink />} />
        <Route path="/intake" element={<LegacyIntakeFormLink />} />

        <Route path={routes.app} element={<LegacyAppView><AuthGate /></LegacyAppView>}>
          <Route index element={<Dashboard />} />
          <Route path="students" element={<Students />} />
          <Route path="ensembles" element={<Ensembles />} />
          <Route path="lessons" element={<Lessons />} />
          <Route path="payments" element={<Payments />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="receipts" element={<Receipts />} />
          <Route path="receipt-reports" element={<ReceiptReports />} />
          <Route path="business" element={<BusinessSettings />} />
          <Route path="open-format" element={<OpenFormat />} />
          <Route path="admin" element={<Admin />} />
          <Route path="reports" element={<Reports />} />
          <Route path="reminders" element={<Reminders />} />
          <Route path="payment-documents" element={<PaymentDocuments />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="backup" element={<Backup />} />
          <Route path="google-calendar" element={<GoogleCalendar />} />
          <Route path="students/:name" element={<StudentProfile />} />
          <Route path="premium" element={<Premium />} />
          <Route path="profile" element={<Profile />} />
          <Route path="*" element={<Page404 />} />
        </Route>

        <Route path={routes.paymentRequest} element={<PaymentRequest />} />
        <Route path={routes.intakeForm} element={<IntakeForm />} />
        <Route path="*" element={<Page404 />} />
      </Route>
    </Routes>
  );
}
